import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { cn } from '@/lib/utils.ts';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps<T> {
  data: T[];
  columns: any;
  loading?: boolean;
  pageSize?: number;
  bodyClass?: string;
  currentPage?: number;
  headerClass?: string;
  onRowClick?: (row: T) => void;
  rowsClassName?: string;
  getRowClassName?: (row: T) => string;
  isCheckable?: boolean;
  selectedRows?: T[];
  setSelectedRows?: Dispatch<SetStateAction<T[]>>;
}
export const GeneralTable = <T,>({
  data,
  columns,
  loading,
  pageSize,
  bodyClass,
  onRowClick,
  rowsClassName,
  getRowClassName,
  headerClass,
  currentPage,
  isCheckable = false,
  selectedRows,
  setSelectedRows,
}: IProps<T>) => {
  const { t } = useTranslation();
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [isHeaderIndeterminate, setIsHeaderIndeterminate] = useState(false);
  useEffect(() => {
    if (selectedRows?.length === 0) {
      setIsHeaderChecked(false);
      setIsHeaderIndeterminate(false);
    } else if (selectedRows?.length === data.length) {
      setIsHeaderChecked(true);
      setIsHeaderIndeterminate(false);
    } else {
      setIsHeaderChecked(true);
      setIsHeaderIndeterminate(true);
    }
  }, [selectedRows, data]);
  const handleHeaderCheckboxChange = () => {
    if (!setSelectedRows) return;
    if (isHeaderChecked || isHeaderIndeterminate) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...data]);
    }
  };

  const handleRowCheckboxChange = (row: T) => {
    if (!setSelectedRows) return;
    setSelectedRows((prevSelectedRows) => {
      const exists = prevSelectedRows.find(
        (selectedRow) => selectedRow === row,
      );
      return exists
        ? prevSelectedRows.filter((selectedRow) => selectedRow !== row)
        : [...prevSelectedRows, row];
    });
  };

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const BodyContent = () => {
    if (loading) {
      return (
        <TableRow className="w-full">
          <TableCell colSpan={columns.length + 1} className="h-2 !p-0">
            <div className="progress" />
          </TableCell>
        </TableRow>
      );
    }

    const { rows } = table.getRowModel();

    if (!rows.length) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length + 1}
            className="h-24 text-center text-gray-500"
          >
            {t('no_results')}
          </TableCell>
        </TableRow>
      );
    }

    return rows?.map((row, index) => (
      <TableRow
        key={row.id}
        className={cn(
          index % 2 ? 'bg-whitesmoke' : 'bg-white',
          onRowClick ? 'cursor-pointer hover:bg-[#2ED0FF1A]' : '',
          rowsClassName,
          getRowClassName?.(row.original as T),
        )}
        onClick={() => onRowClick && onRowClick(row.original as T)}
      >
        {isCheckable && (
          <TableCell>
            <Checkbox
              checked={selectedRows?.includes(row.original)}
              onCheckedChange={() => handleRowCheckboxChange(row.original)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}
        {row.getVisibleCells()?.map((cell) => (
          <TableCell key={cell.id} className="px-3 py-2">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <Table>
      <TableHeader className={cn('bg-gray-200', headerClass)}>
        {table.getHeaderGroups()?.map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {isCheckable && (
              <TableHead className="w-[10px]">
                <Checkbox
                  checked={isHeaderChecked}
                  onCheckedChange={handleHeaderCheckboxChange}
                  className="border-gray-300 bg-white"
                />
              </TableHead>
            )}

            {headerGroup.headers?.map((header) => (
              <TableHead key={header.id}>
                {!header.isPlaceholder &&
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className={bodyClass}>
        <BodyContent />
      </TableBody>
    </Table>
  );
};
